
import {
  defineComponent,
  defineExpose,
  reactive,
  ref,
  Ref,
  onMounted,
} from 'vue';

import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import Loader from '@/components/layout/Loader.vue';

import { saveShopParamsOnRedis, fetchShopConfigurationsNest, saveShopConfigurationsNest } from '@/composables/shop/ShopsConfiguration';
import useVuelidate from '@vuelidate/core';
import { ErrorConfigForm } from '@/types/automated-scenarios';
import { ShopsConfigurationInputItem } from '@/types/generated-types/graphql';
import { showToastError, showToastSuccess } from '@/helpers';

import { serialize } from 'php-serialize';

import { useI18n } from 'vue-i18n';
import { NestApiType, nestCall } from '@/composables/nestApi';
import { useFacebookLogin } from '@/composables/facebook/useFacebookLogin';
import WhatsappOnboardingWizard from '@/components/whatsapp-onboarding/WhatsappOnboardingWizard.vue';

export default defineComponent({
  name: 'WhatsappBusinessServiceParameter',

  components: {
    Loader,
    Button,
    Dialog,
    WhatsappOnboardingWizard,
  },

  props: {
    shopId: {
      type: Number,
      required: true,
    },

    globalSave: {
      type: Boolean,
      required: false,
      default: false,
    },

    name: {
      type: String,
      required: false,
      default: '',
    },
  },

  emits: ['whatsapp-account-connected'],

  setup(props, { emit }) {
    const { t } = useI18n();

    const isLoading = ref(false);
    const isConnecting = ref(false);

    const isUnlinking = ref(false);
    const isUnlinkConfirmVisible = ref(false);
    const isUnlinkImpactLoading = ref(false);
    const unlinkImpact = ref<{
      templatesToReset: number;
      automationStepsToDisable: number;
      bulkCampaignsToDraft: number;
    } | null>(null);
    const isDetailsLoading = ref(false);

    const isWhatsappAccountConnected = ref(false);

    const { loginAndConnectWABA } = useFacebookLogin(
      props.shopId,
      process.env.VUE_APP_FACEBOOK_WHATSAPP_APP_ID || '685038527618616',
      process.env.VUE_APP_FACEBOOK_WHATSAPP_GRAPH_VERSION || 'v25.0',
    );

    const componentFieldErrorsKey = ref(0);
    const error = ref();

    const field = reactive<Record<string, any>>({
      shopRequiredData_whatsapp_business: {},
    });
    const wabaBusinessDetails = reactive<Record<string, any>>({
      verified_name: '',
      code_verification_status: '',
      waba_id: '',
      display_phone_number: '',
      number_id: '',
      messaging_limit_tier: '',
      quality_score: {
        score: '',
      },
      business_verification_status: '',
      account_status: '',
      live_unavailable: true,
    });

    const validate = async (): Promise<ErrorConfigForm> => {
      const rules: Record<string, any> = {
        shopRequiredData_whatsapp_business: {},
      };

      const v$ = useVuelidate(rules, field);
      const success = await v$.value.$validate();

      return {
        success,
        validate: v$,
      };
    };

    const fetchWhatsappInformations = async (options?: { manageLoading?: boolean }) => {
      const manageLoading = options?.manageLoading !== false;
      try {
        if (manageLoading) {
          isLoading.value = true;
        }
        field.shopRequiredData_whatsapp_business = {};

        // Fetch configurations from Nest REST API
        const keys = Object.keys(field);
        const data = await fetchShopConfigurationsNest(props.shopId, keys);

        if (data) {
          // Map API response to field
          Object.keys(data).forEach((key) => {
            if (key in field) {
              field[key] = data[key];
            }
          });
        }

        const whatsappBusiness = field.shopRequiredData_whatsapp_business;

        if (whatsappBusiness) {
          const parsedWhatsappBusiness =
            typeof whatsappBusiness === 'string'
              ? JSON.parse(whatsappBusiness)
              : whatsappBusiness;
          isWhatsappAccountConnected.value = Object.keys(parsedWhatsappBusiness).length > 0;
          field.shopRequiredData_whatsapp_business = parsedWhatsappBusiness;
          if (isWhatsappAccountConnected.value) {
            await fetchWhatsappBusinessDetails();
          }
          // Emit event to parent with current connection status
          emit('whatsapp-account-connected', isWhatsappAccountConnected.value);
        } else {
          isWhatsappAccountConnected.value = false;
        }
      } catch (err) {
      } finally {
        if (manageLoading) {
          isLoading.value = false;
        }
      }
    };

    const hydrateFallbackDetailsFromConfig = () => {
      const config = field.shopRequiredData_whatsapp_business || {};
      wabaBusinessDetails.verified_name = config.business_name || '';
      wabaBusinessDetails.code_verification_status = '';
      wabaBusinessDetails.waba_id = config.waba_id || '';
      wabaBusinessDetails.display_phone_number = config.phone_number || '';
      wabaBusinessDetails.number_id = config.phone_number_id || '';
      wabaBusinessDetails.messaging_limit_tier = '';
      wabaBusinessDetails.quality_score = { score: '' };
      wabaBusinessDetails.business_verification_status = String(
        config.business_verification_status || '',
      ).toUpperCase();
      wabaBusinessDetails.account_status = String(
        config.account_status || '',
      ).toUpperCase();
      wabaBusinessDetails.live_unavailable = true;
    };

    const fetchWhatsappBusinessDetails = async () => {
      isDetailsLoading.value = true;
      hydrateFallbackDetailsFromConfig();
      try {
        const response = await nestCall(
          'get',
          NestApiType.MESSAGING,
          '/whatsapp/business-account-details',
          {},
          '',
          undefined,
          true,
          { shop_id: Number(props.shopId) },
        );

        const details = response?.data || null;
        if (!details) {
          return;
        }

        wabaBusinessDetails.verified_name = details.verified_name || wabaBusinessDetails.verified_name;
        wabaBusinessDetails.code_verification_status = details.code_verification_status || '';
        wabaBusinessDetails.waba_id = details.waba_id || wabaBusinessDetails.waba_id;
        wabaBusinessDetails.display_phone_number = details.display_phone_number || wabaBusinessDetails.display_phone_number;
        wabaBusinessDetails.number_id = details.number_id || wabaBusinessDetails.number_id;
        wabaBusinessDetails.messaging_limit_tier = details.messaging_limit_tier || '';
        wabaBusinessDetails.quality_score = {
          score: details?.quality_score?.score || '',
        };
        wabaBusinessDetails.business_verification_status = String(
          details.business_verification_status || '',
        ).toUpperCase();
        wabaBusinessDetails.account_status = String(
          details.account_status || '',
        ).toUpperCase();
        wabaBusinessDetails.live_unavailable = !!details.live_unavailable;
      } catch (err) {
        hydrateFallbackDetailsFromConfig();
      } finally {
        isDetailsLoading.value = false;
      }
    };

    const getDisplayValue = (value: string | null | undefined): string => (
      value && String(value).trim() !== ''
        ? String(value)
        : t('storeParameters.services.whatsappBusinessParameter.connected.valueUnavailable')
    );

    const getCodeVerificationLabel = (status: string): string => {
      const normalized = String(status || '').toUpperCase();
      if (normalized === 'VERIFIED') return t('storeParameters.services.whatsappBusinessParameter.connected.verificationStatus.verified');
      if (normalized === 'NOT_VERIFIED') return t('storeParameters.services.whatsappBusinessParameter.connected.verificationStatus.notVerified');
      if (normalized === 'EXPIRED') return t('storeParameters.services.whatsappBusinessParameter.connected.verificationStatus.expired');
      return t('storeParameters.services.whatsappBusinessParameter.connected.verificationStatus.unknown');
    };

    const getCodeVerificationBadgeClass = (status: string): string => {
      const normalized = String(status || '').toUpperCase();
      if (normalized === 'VERIFIED') return 'waba__badge--success';
      if (normalized === 'NOT_VERIFIED') return 'waba__badge--warning';
      if (normalized === 'EXPIRED') return 'waba__badge--danger';
      return 'waba__badge--neutral';
    };

    const getTierLabel = (tier: string): string => {
      const normalized = String(tier || '').toUpperCase();
      if (!normalized) return '-';
      return t(`storeParameters.services.whatsappBusinessParameter.connected.messagingTiers.${normalized}`);
    };

    const getTierBadgeClass = (tier: string): string => {
      const normalized = String(tier || '').toUpperCase();
      if (normalized === 'TIER_100K' || normalized === 'TIER_UNLIMITED') return 'waba__badge--success';
      if (normalized === 'TIER_10K' || normalized === 'TIER_1K') return 'waba__badge--info';
      if (normalized === 'TIER_250' || normalized === 'TIER_50') return 'waba__badge--warning';
      return 'waba__badge--neutral';
    };

    const getQualityLabel = (score: string): string => {
      const normalized = String(score || '').toUpperCase();
      if (normalized === 'GREEN') return t('storeParameters.services.whatsappBusinessParameter.connected.qualityScores.green');
      if (normalized === 'YELLOW') return t('storeParameters.services.whatsappBusinessParameter.connected.qualityScores.yellow');
      if (normalized === 'RED') return t('storeParameters.services.whatsappBusinessParameter.connected.qualityScores.red');
      return t('storeParameters.services.whatsappBusinessParameter.connected.qualityScores.unknown');
    };

    const getQualityBadgeClass = (score: string): string => {
      const normalized = String(score || '').toUpperCase();
      if (normalized === 'GREEN') return 'waba__badge--success';
      if (normalized === 'YELLOW') return 'waba__badge--warning';
      if (normalized === 'RED') return 'waba__badge--danger';
      return 'waba__badge--neutral';
    };

    const getWabaAccountStatusLabel = (status: string): string => {
      const normalized = String(status || '').toUpperCase();
      if (!normalized) {
        return t('storeParameters.services.whatsappBusinessParameter.connected.wabaAccountStatuses.unknown');
      }
      return t(
        `storeParameters.services.whatsappBusinessParameter.connected.wabaAccountStatuses.${normalized}`,
        t('storeParameters.services.whatsappBusinessParameter.connected.wabaAccountStatuses.unknown'),
      );
    };

    const getWabaAccountStatusBadgeClass = (status: string): string => {
      const normalized = String(status || '').toUpperCase();
      if (normalized === 'ACTIVE' || normalized === 'CONNECTED') return 'waba__badge--success';
      if (
        normalized === 'BANNED'
        || normalized === 'RESTRICTED'
        || normalized === 'DELETED'
        || normalized === 'FLAGGED'
      ) return 'waba__badge--danger';
      if (
        normalized === 'INELIGIBLE'
        || normalized === 'INELIGIBLE_ON_BEHALF_OF_WABA'
        || normalized === 'PENDING'
        || normalized === 'UNVERIFIED'
        || normalized === 'NOT_STARTED'
        || normalized === 'RATE_LIMITED'
        || normalized === 'OFFLINE'
        || normalized === 'DISCONNECTED'
        || normalized === 'MIGRATED'
      ) return 'waba__badge--warning';
      return 'waba__badge--neutral';
    };

    const getBusinessStatusLabel = (status: string): string => {
      const normalized = String(status || '').toUpperCase();
      if (!normalized) return t('storeParameters.services.whatsappBusinessParameter.connected.businessStatuses.unknown');
      return t(
        `storeParameters.services.whatsappBusinessParameter.connected.businessStatuses.${normalized}`,
        t('storeParameters.services.whatsappBusinessParameter.connected.businessStatuses.unknown'),
      );
    };

    const getBusinessStatusBadgeClass = (status: string): string => {
      const normalized = String(status || '').toUpperCase();
      if (normalized === 'VERIFIED') return 'waba__badge--success';
      if (
        normalized === 'REJECTED'
        || normalized === 'REVOKED'
        || normalized === 'FAILED'
        || normalized === 'EXPIRED'
      ) return 'waba__badge--danger';
      if (
        normalized === 'PENDING'
        || normalized === 'PENDING_NEED_MORE_INFO'
        || normalized === 'PENDING_SUBMISSION'
      ) return 'waba__badge--warning';
      return 'waba__badge--neutral';
    };

    // eslint-disable-next-line consistent-return
    const handleSave = async () => {
      const validation = await validate();
      if (!validation.success) {
        error.value = validation.validate;
        componentFieldErrorsKey.value += 1;
        return {
          err: validation.validate.value.$errors.length,
          configs: [],
        };
      }

      // Prepare configurations for Nest API
      // WhatsApp data is saved as a single JSON string, not split by lang
      const configurationsToSave: Array<{ key: string; value: any; lang: string | null }> = [];

      Object.entries(field).forEach(([key, value]) => {
        configurationsToSave.push({
          key,
          value: typeof value === 'object' ? JSON.stringify(value) : serialize(value),
          lang: null,
        });
      });

      if (props.globalSave) {
        // For global save, return GraphQL format for compatibility
        const shopsConfigArray: ShopsConfigurationInputItem[] = configurationsToSave.map((config) => ({
          key: config.key,
          value: config.value,
          lang: config.lang || '',
        }));
        return {
          err: null,
          configs: shopsConfigArray,
        };
      }

      if (configurationsToSave.length) {
        try {
          const result = await saveShopConfigurationsNest(props.shopId, configurationsToSave);
          if (result) {
            await saveShopParamsOnRedis(props.shopId);
          } else {
            showToastError('GENERIC_ERROR');
          }
        } catch (err) {
          showToastError('GENERIC_ERROR');
        }
      }
    };

    const handleLinkWhatsappAccount = async () => {
      try {
        await loginAndConnectWABA(async () => {
          await fetchWhatsappInformations({ manageLoading: false });
          if (!props.globalSave) {
            await handleSave();
          }
        }, {
          onBackendWorkStart: () => {
            isConnecting.value = true;
          },
        });
      } catch (err) {
        // Errors are toasted in useFacebookLogin
      } finally {
        isConnecting.value = false;
      }
    };

    const fetchUnlinkImpact = async () => {
      isUnlinkImpactLoading.value = true;
      unlinkImpact.value = null;

      try {
        const response = await nestCall(
          'get',
          NestApiType.MESSAGING,
          '/whatsapp/unlink-impact',
          {},
          '',
          undefined,
          true,
          { shop_id: Number(props.shopId) },
        );

        if (response?.data) {
          unlinkImpact.value = response.data;
        }
      } catch (err) {
        showToastError('GENERIC_ERROR');
      } finally {
        isUnlinkImpactLoading.value = false;
      }
    };

    const handleUnlinkWhatsappAccount = async () => {
      isUnlinkConfirmVisible.value = true;
      await fetchUnlinkImpact();
    };

    const confirmUnlinkWhatsappAccount = async () => {
      isUnlinking.value = true;
      try {
        const response = await nestCall('post', NestApiType.MESSAGING, '/whatsapp/unlink-whatsapp-account', {}, '', {
          shop_id: Number(props.shopId),
        });
        if (response?.error) {
          showToastError('GENERIC_ERROR');
          return;
        }
        showToastSuccess('savedSuccessful');
        isUnlinkConfirmVisible.value = false;
        await fetchWhatsappInformations();
      } catch (err) {
        showToastError('GENERIC_ERROR');
      } finally {
        isUnlinking.value = false;
      }
    };

    defineExpose({
      handleSave,
    });

    onMounted(async () => {
      await fetchWhatsappInformations();
    });

    return {
      t,
      field,
      error,
      componentFieldErrorsKey,
      isLoading,
      isConnecting,
      isUnlinking,
      handleLinkWhatsappAccount,
      handleUnlinkWhatsappAccount,
      confirmUnlinkWhatsappAccount,
      isWhatsappAccountConnected,
      handleSave,
      isUnlinkConfirmVisible,
      isUnlinkImpactLoading,
      unlinkImpact,
      isDetailsLoading,
      wabaBusinessDetails,
      getDisplayValue,
      getCodeVerificationLabel,
      getCodeVerificationBadgeClass,
      getTierLabel,
      getTierBadgeClass,
      getQualityLabel,
      getQualityBadgeClass,
      getBusinessStatusLabel,
      getBusinessStatusBadgeClass,
      getWabaAccountStatusLabel,
      getWabaAccountStatusBadgeClass,
    };
  },
});
